import React from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { parseNumber, strict } from "@apl-digital/utils";
import * as Sentry from "@sentry/react";

import config from "@/constants/config";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  enableLogs: true,
  enableMetrics: true,
  normalizeDepth: 10,
  ignoreErrors: [
    // Browser-specific noise
    "ResizeObserver loop",
    // Network errors from flaky connections
    "Network request failed",
    "Failed to fetch",
    "NetworkError",
    "Load failed",
    "TypeError: cancelled",
    // Chunk loading failures (user on stale version after deploy)
    "ChunkLoadError",
    /Loading chunk .* failed/,
    /Loading CSS chunk .* failed/,
    // Aborted requests (user navigated away)
    "AbortError",
    "The operation was aborted",
    "signal is aborted without reason",
    // Browser permission/security errors not actionable
    "SecurityError",
    "NotAllowedError",
  ],
  denyUrls: [
    // Browser extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-extension:\/\//i,
    /^safari-web-extension:\/\//i,
  ],
  beforeSend(event, hint) {
    const error = hint.originalException;

    // Filter out errors from third-party scripts injected into the page
    if (
      event.exception?.values?.some((exception) =>
        exception.stacktrace?.frames?.some(
          (frame) =>
            frame.filename &&
            (frame.filename.includes("chrome-extension://") ||
              frame.filename.includes("moz-extension://") ||
              frame.filename.includes("safari-extension://")),
        ),
      )
    ) {
      return null;
    }

    // Filter out non-error promise rejections (e.g., rejected with a string or undefined)
    if (
      error &&
      typeof error !== "object" &&
      event.exception?.values?.[0]?.mechanism?.type === "onunhandledrejection"
    ) {
      return null;
    }

    return event;
  },
  integrations: [
    Sentry.browserProfilingIntegration(),
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      maskAllInputs: true,
    }),
    Sentry.extraErrorDataIntegration({
      captureErrorCause: true,
    }),
  ],
  tracePropagationTargets: ["localhost", config.apiConfig.orderAppProxy?.url],
  profilesSampleRate: strict(
    parseNumber(import.meta.env.VITE_SENTRY_PROFILES_SAMPLE_RATE),
  ),

  // Performance Monitoring
  tracesSampleRate: strict(
    parseNumber(import.meta.env.VITE_SENTRY_TRACE_SAMPLE_RATE),
  ),

  // Session Replay
  replaysSessionSampleRate: strict(
    parseNumber(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE),
  ),
  replaysOnErrorSampleRate: strict(
    parseNumber(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE),
  ),
});
